import { useApi } from '@/composables/api.ts'
import type { FileTree } from '@/types/file-tree.ts'

export const useDeepBoxDeepBoxesBoxesFilesTreeStore = defineStore(
  'deepBoxDeepBoxesBoxesFilesTreeStore',
  () => {
    const { deepBoxAPI } = useApi()

    const isFilesTreeOpened = ref(false)
    const fetchFilesTreePending = ref(false)
    const fetchFilesTreeChildrenPending = ref(false)
    const filesTree = ref<FileTree[]>([])

    async function fetchFilesTree(payload: {
      typeId: string
      boxId: string
      parent?: FileTree
    }) {
      fetchFilesTreePending.value = true
      try {
        const res = await deepBoxAPI.value.deepBoxesBoxesFilesPath.get(
          payload.typeId,
          payload.boxId,
        )

        if (res.data && filesTree.value.length) {
          updateTreeReferences(
            (res.data?.children ?? []) as FileTree[],
            payload.parent?.children ?? filesTree.value[0].children,
            // add 'files' node id as initial parentId
            payload.parent?.nodeId ?? filesTree.value[0].nodeId,
          )
        }

        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      } finally {
        fetchFilesTreePending.value = false
      }
    }

    async function fetchFilesTreeChildren(payload: {
      typeId: string
      boxId: string
      nodeId: string
      parent?: FileTree
    }) {
      fetchFilesTreeChildrenPending.value = true
      try {
        const res = await deepBoxAPI.value.deepBoxesBoxesFilesPath.getById(
          payload.typeId,
          payload.boxId,
          payload.nodeId,
        )

        if (payload?.parent && res.data?.children) {
          if (!payload.parent?.children) {
            payload.parent.children = []
          }

          const fetchedChildren = res.data.children

          updateTreeReferences(
            fetchedChildren as FileTree[],
            payload.parent.children,
            payload.parent.nodeId,
          )
        }

        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      } finally {
        fetchFilesTreeChildrenPending.value = false
      }
    }

    function updateTreeReferences(
      fetchedNodes: FileTree[],
      currentReferenceNodes: FileTree[],
      parentNodeId?: string,
    ) {
      // used to sort the children like the fetched ones
      const sortIndexes: {
        [nodeId: string]: number
      } = {}

      fetchedNodes.forEach((fetchedNode, index) => {
        const referenceNode = currentReferenceNodes.find(
          (c) => c.nodeId === fetchedNode.nodeId,
        )
        sortIndexes[fetchedNode.nodeId] = index
        if (parentNodeId && !fetchedNode.parentNodeId) {
          fetchedNode.parentNodeId = parentNodeId
        }
        if (referenceNode) {
          // update reference node, but do not overwrite the children because
          // missing in the fetched data
          for (const key in fetchedNode) {
            if (key !== 'children') {
              referenceNode[key] = fetchedNode[key]
            }
          }
        } else {
          // the fetched child is not present in the reference yet, so add it
          fetchedNode.children = []
          currentReferenceNodes.push(fetchedNode)
        }
      })

      // sort children like the fetched ones
      currentReferenceNodes.sort((a, b) => {
        return sortIndexes[a.nodeId] - sortIndexes[b.nodeId]
      })

      // remove children from the reference which are not present in the
      // fetched data
      const childIndexesToRemove: number[] = []
      currentReferenceNodes.forEach((child, referenceIndex) => {
        const findNode = fetchedNodes.find((c) => c.nodeId === child.nodeId)
        if (!findNode) {
          childIndexesToRemove.push(referenceIndex)
        }
      })
      // reverse the indexes array, because on splice the size of the array
      // changes
      childIndexesToRemove.reverse()
      childIndexesToRemove.forEach((index) => {
        currentReferenceNodes.splice(index, 1)
      })
    }

    function getFileTreeNodeByNodeId(
      nodeId: string,
      items: FileTree[],
    ): FileTree | undefined {
      for (const n of items) {
        if (n.nodeId === nodeId) return n

        if (n.children?.length) {
          const res = getFileTreeNodeByNodeId(nodeId, n.children)
          if (res) return res
        }
      }
    }

    return {
      // state
      isFilesTreeOpened,
      fetchFilesTreePending,
      fetchFilesTreeChildrenPending,
      filesTree,
      // getters
      getFileTreeNodeByNodeId,
      // actions
      fetchFilesTree,
      fetchFilesTreeChildren,
    }
  },
)

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useDeepBoxDeepBoxesBoxesFilesTreeStore, import.meta.hot),
  )
}
